import { ComponentRendering } from '@sitecore-content-sdk/nextjs';
import useIsEditing from 'lib/hooks/useIsEditing';
import {
  createContext,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';

const SideNavStateContext = createContext<ComponentRendering | undefined>(undefined);
const SideNavApiContext = createContext<{
  registerIntersection: (uid: string, isIntersecting: boolean) => void;
}>({
  registerIntersection: () => {},
});

export function useSideNavApi() {
  return useContext(SideNavApiContext);
}

export function useSideNavContext() {
  const activeEntry = useContext(SideNavStateContext);
  const { registerIntersection } = useContext(SideNavApiContext);
  return { activeEntry, registerIntersection };
}

interface SideNavContextProviderProps<TEntry> extends React.PropsWithChildren {
  entries: TEntry[];
}

export function SideNavContextProvider<TEntry extends ComponentRendering>({
  children,
  entries,
}: SideNavContextProviderProps<TEntry>) {
  const [activeEntry, setActiveEntry] = useState<TEntry>();
  const intersectionMap = useRef<Map<string, boolean>>(new Map());

  const entriesRef = useRef(entries);
  useEffect(() => {
    entriesRef.current = entries;
  }, [entries]);

  const checkForActiveEntry = useCallback(() => {
    const firstVisibleEntry = entriesRef.current.find(
      (entry) => entry.uid && intersectionMap.current.get(entry.uid) === true
    );

    if (firstVisibleEntry) {
      setActiveEntry((prev) => {
        if (prev?.uid !== firstVisibleEntry.uid) {
          return firstVisibleEntry;
        }
        return prev;
      });
    }
  }, []);

  const registerIntersection = useCallback(
    (uid: string, isIntersecting: boolean) => {
      intersectionMap.current.set(uid, isIntersecting);
      checkForActiveEntry();
    },
    [checkForActiveEntry]
  );

  useEffect(() => {
    if (!activeEntry && entries.length > 0) {
      setActiveEntry(entries[0]);
    }
  }, [entries, activeEntry]);

  const apiValue = useMemo(() => ({ registerIntersection }), [registerIntersection]);

  return (
    <SideNavApiContext.Provider value={apiValue}>
      <SideNavStateContext.Provider value={activeEntry}>{children}</SideNavStateContext.Provider>
    </SideNavApiContext.Provider>
  );
}

/**
 * Watches the target item to update the activeEntry
 * @param itemRef The ref to the item to watch
 * @param uid The UID of the active entry
 */
export function useWatchSideNavActiveState(
  itemRef: RefObject<HTMLElement | null>,
  uid: string | undefined
) {
  const { registerIntersection } = useSideNavApi();
  const isEditingData = useIsEditing();

  useEffect(() => {
    if (isEditingData || !uid || !itemRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        registerIntersection(uid, entry.isIntersecting);
      },
      {
        rootMargin: '-10% 0px -80% 0px', // Active margin that intersection is calculated based on
        threshold: 0,
      }
    );

    observer.observe(itemRef.current);

    return () => {
      observer.disconnect();
    };
  }, [registerIntersection, itemRef, uid, isEditingData]);
}
